/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import { ChipProps } from 'components/ChoreoSystem/Chip/Chip';
import { RenderTree } from 'components/ChoreoSystem/Tree/Tree.data';
import { DeploymentStatus } from './environment';
import { Environment } from './org';
import { BuildEnvironmentVariable } from './app-configs';
import { APICreationInfo } from './api';

/*
  All the Project Component related types
*/
export enum BuildPresetType {
  BALLERINA = 'Ballerina',
  DOCKERFILE = 'Dockerfile',
  MICRO_INTEGRATION = 'WSO2 MI',
  WEB_APP_REACT = 'React',
  WEB_APP_ANGULAR = 'Angular',
  WEB_APP_VUEJS = 'Vuejs',
  WEB_APP_STATIC_FILES = 'StaticFiles',
  BYOI = 'BYOI',
  TEST_RUNNER_POSTMAN = 'Postman',
  BUILDPACK = 'Buildpack',
  PRISM_MOCK = 'PrismMock',
}

export enum ComponentTemplateType {
  HttpApi = 'httpApi',
  HttpProxyApi = 'httpProxyApi',
  ManualTrigger = 'manualTrigger',
  ScheduleTask = 'scheduleTask',
  GraphqlApi = 'graphqlApi',
  GraphqlProxyApi = 'graphqlProxyApi',
  WebSocket = 'webSocket',
  WebSocketProxy = 'webSocketProxy',
  Webhook = 'webhook',
  GrpcApi = 'grpcApi',
  CustomApp = 'customApp',
  ByoContainer = 'byoContainer',
  GraphQL = 'graphql',
  ThirdPartyApi = 'thirdPartyApi',
  Microservices = 'microservices',
  WebApp = 'webApp',
  IntegrationApi = 'IntegrationApi',
  IntegrationWebhook = 'IntegrationWebhook',
  BallerinaServiceWebhookSubtype = 'BallerinaServiceWebhookSubtype',
  IntegrationScheduledTask = 'IntegrationScheduledTask',
  IntegrationManualTriggers = 'IntegrationManualTriggers',
  TestRunnerComponent = 'testRunnerComponent',
  ExternalConsumer = 'externalConsumer',
  PrismMockServiceComponent = 'prismMockService',
  FileIntegration = 'fileIntegration',
  AiAgent = 'aiAgent',
  MCPServer = 'mcpService',
  MCPProxyFromExistingAPIs = 'MCPProxyFromExistingAPI',
  MCPProxyFromEndpoint = 'MCPProxyFromEndpoint',
}

export enum ComponentDisplayName {
  MICROSERVICE = 'Integration as API',
  SCHEDULED_TRIGGER = 'Automation',
  SCHEDULED_TASK = 'Automation',
  MANUAL_TRIGGER = 'Manual Trigger',
  MANUAL_TASK = 'Manual Task',
  HTTP_PROXY = 'REST API Proxy',
  WEBHOOK = 'Webhook',
  INTEGRATION_AS_AN_API = 'Integration as an API',
  EVENT_INTEGRATION = 'Event Integration',
  FILE_INTEGRATION = 'File Integration',
  INTEGRATION_WEBHOOK = 'IntegrationWebhook',
  INTEGRATION_MANUAL_TRIGGER = 'Manually-Triggered Integration',
  INTEGRATION_SCHEDULED_TRIGGER = 'Scheduled Integration',
  THIRD_PARTY_API = 'Integration as API',
  WEB_APP = 'Web Application',
  TEST_RUNNER = 'Test Runner',
  EXTERNAL_CONSUMER = 'External Consumer',
  AI_AGENT = 'AI Agent',
  MCP_SERVER = 'MCP Server',
  TAILSCALE_VPN = 'Tailscale VPN',
  RAG_INGESTION = 'RAG Ingestion',
  VOICE_AGENT = 'Voice Agent',
  RAG_KNOWLEDGE_BASE = 'KB as API',
}

export enum DevantComponentDisplayType {
  RestApi = 'restAPI',
  ManualTrigger = 'manualTrigger',
  ScheduledTask = 'scheduledTask',
  Webhook = 'webhook',
  MiRestApi = 'miRestApi',
  MiEventHandler = 'miEventHandler',
  Service = 'ballerinaService',
  MiApiService = 'miApiService',
  MiCronjob = 'miCronjob',
  MiJob = 'miJob',
  MiWebhook = 'miWebhook',
  BallerinaEventHandler = 'ballerinaEventHandler',
  BallerinaService = 'ballerinaService',
  BallerinaWebhook = 'ballerinaWebhook',
  BallerinaFileIntegration = 'ballerinaFileIntegration',
  MiFileIntegration = 'miFileIntegration',
  AiAgent = 'aiAgent',
  MCPServer = 'MCP',
}

export enum ComponentDisplayType {
  RestApi = 'restAPI',
  ManualTrigger = 'manualTrigger',
  ScheduledTask = 'scheduledTask',
  Webhook = 'webhook',
  Websocket = 'webSocket',
  Proxy = 'proxy',
  GitProxy = 'gitProxy',
  ByocCronjob = 'byocCronjob',
  ByocJob = 'byocJob',
  GraphQL = 'graphql',
  ThirdPartyAPI = 'thirdPartyAPI',
  ByocWebApp = 'byocWebApp',
  ByocWebAppDockerLess = 'byocWebAppsDockerfileLess',
  ByocRestApi = 'byocRestApi',
  ByocEventHandler = 'byocEventHandler',
  MiRestApi = 'miRestApi',
  MiEventHandler = 'miEventHandler',
  Service = 'ballerinaService',
  ByocService = 'byocService',
  ByocWebhook = 'byocWebhook',
  MiApiService = 'miApiService',
  MiCronjob = 'miCronjob',
  MiJob = 'miJob',
  MiWebhook = 'miWebhook',
  ByoiService = 'byoiService',
  ByoiJob = 'byoiJob',
  ByoiCronjob = 'byoiCronjob',
  ByoiWebApp = 'byoiWebApp',
  ByocTestRunner = 'byocTestRunner',
  BuildpackService = 'buildpackService',
  BuildpackEventHandler = 'buildpackEventHandler',
  BuildpackJob = 'buildpackJob',
  BuildpackTestRunner = 'buildpackTestRunner',
  BuildpackCronJob = 'buildpackCronjob',
  BuildpackWebApp = 'buildpackWebApp',
  BuildpackWebhook = 'buildpackWebhook',
  BuildRestApi = 'buildpackRestApi',
  PostmanTestRunner = 'byocTestRunnerDockerfileLess',
  BallerinaEventHandler = 'ballerinaEventHandler',
  BallerinaService = 'ballerinaService',
  BallerinaWebhook = 'ballerinaWebhook',
  ExternalConsumer = 'externalConsumer',
  PrismMockService = 'prismMockService',
  BallerinaFileIntegration = 'ballerinaFileIntegration',
  MiFileIntegration = 'miFileIntegration',
  AiAgent = 'aiAgent',
  MCPServer = 'MCP',
  MCPProxyFromExistingAPI = 'MCPProxyFromExistingAPI',
  MCPProxyFromExistingAPIWithSource = 'MCPProxyFromExistingAPIWithSource',
  TailscaleVpn = 'tailscale',
  RagIngestion = 'rag-ingestion',
  RagKnowledgeBase = 'knowledge-base',
}
export type ComponentDisplayBYOCTypes =
  | ComponentDisplayType.ByocCronjob
  | ComponentDisplayType.ByocJob
  | ComponentDisplayType.ByocWebApp
  | ComponentDisplayType.ByocRestApi
  | ComponentDisplayType.ByocEventHandler;

export type ComponentDisplayBuildpackTypes =
  | ComponentDisplayType.BuildpackService
  | ComponentDisplayType.BuildpackWebApp
  | ComponentDisplayType.BuildpackJob
  | ComponentDisplayType.BuildpackTestRunner
  | ComponentDisplayType.BuildRestApi
  | ComponentDisplayType.BuildpackCronJob
  | ComponentDisplayType.PrismMockService;

export enum ApiAccessMode {
  External = 'external',
  Internal = 'internal',
}

export enum ComponentCreationStatus {
  QUEUED = 'queued',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILURE = 'failure',
}

export enum BuildPackLanguage {
  Ballerina = 'ballerina',
  Java = 'java',
  Go = 'go',
  NodeJs = 'nodejs',
  Python = 'python',
  Ruby = 'ruby',
  Rust = 'rust',
  Other = 'other',
}

export enum ComponentType {
  SERVICE = 'service',
  WEB_APP = 'web-app',
  SCHEDULED_TASK = 'scheduled-task',
  MANUAL_TASK = 'manual-task',
  API_PROXY = 'api-proxy',
  WEB_HOOK = 'web-hook',
  EVENT_HANDLER = 'event-handler',
  FILE_HANDLER = 'file-handler',
  TEST = 'test',
  EXTERNAL_CONSUMER = 'external-consumer',
  AI_AGENT = 'ai-agent',
  MCP_SERVER = 'mcp-server',
}

export enum ServiceType {
  HTTP = 'HTTP',
  GRPC = 'GRPC',
  WEBSOCKET = 'Websocket',
  GRAPHQL = 'GraphQL',
  WEBHOOK = 'Webhook',
  WEBAPP = 'WebApp',
  OTHER = 'other',
}

export enum IntegrationCreationMode {
  CREATE_NEW_INTEGRATION = 'create-new-integration',
  BRING_YOUR_INTEGRATION = 'bring-your-integration',
  ATTACH_NEW_INTEGRATION = 'attach-repository',
}

export interface NewComponent {
  displayName: string;
  componentName: string;
  moduleName: string;
  displayType: string;
  description: string;
  apiId: string;
  triggerID: number | null;
  triggerChannels: string;
  sampleTemplate: string;
  version: string;
  httpBase: boolean;
  buildPreset?: BuildPresetType;
  srcGitRepoUrl?: string;
  accessibility?: ApiAccessMode;
  repositorySubPath?: string;
  repositoryType?: string;
  repositoryBranch?: string;
  isGeneratePR?: boolean;
  oasFilePath?: string;
  bitbucketCredentialId?: string;
  bitbucketType?: string;
  isPublicRepo?: boolean;
  enableAutoDeploy?: boolean;
  enableAutoBuild?: boolean;
  componentSubType?: string;
  isPrebuilt?: boolean;
}

export interface NewExternalConsumerComponent {
  displayName: string;
  componentName: string;
  displayType: ComponentDisplayType;
  description: string;
  version: string;
  serviceAccessMode: NetworkVisibilityType;
}

export interface NewByocComponent {
  displayName: string;
  componentName: string;
  description: string;
  labels: string;
  componentType: string;
  port: number | null;
  oasFilePath: string;
  accessibility: string;
  byocConfig: {
    dockerfilePath: string;
    dockerContext: string;
    srcGitRepoUrl: string;
    srcGitRepoBranch: string;
  };
  bitbucketCredentialId?: string;
  buildPreset?: BuildPresetType;
  isPublicRepo?: boolean;
  enableAutoDeploy?: boolean;
  enableAutoBuild?: boolean;
  userProvidedEndpoint?: UserProvidedEndpoint;
}

export interface NewBuildpackComponent {
  displayName: string;
  componentName: string;
  description: string;
  labels: string;
  componentType: string;
  port: number | null;
  oasFilePath: string;
  accessibility: string;
  bitbucketCredentialId?: string;
  buildPreset?: BuildPresetType;
  buildpackConfig: {
    buildContext: string;
    srcGitRepoUrl: string;
    srcGitRepoBranch: string;
    languageVersion: string;
    buildpackId: string;
  };
  isAsyncCreationEnabled?: boolean;
  isPublicRepo?: boolean;
  enableAutoDeploy?: boolean;
  enableAutoBuild?: boolean;
  userProvidedEndpoint?: UserProvidedEndpoint;
}

export interface NewMCPServerComponent {
  displayName: string;
  componentName: string;
  description: string;
  buildPackId: string;
  labels: string;
  serverType: string;
  runCommand: string;
}

export interface CommonComponentInfo {
  componentDisplayName: string;
  componentName: string;
  componentDescription: string;
}

export interface UserProvidedEndpoint {
  id?: string;
  port: number;
  type?: string;
  basePath?: string;
  schemaFilePath?: string;
  displayName?: string;
  networkVisibilities?: NetworkVisibilityType[];
}

export interface NewProxyComponent
  extends CommonComponentInfo,
    APICreationInfo {
  displayType: string;
  httpBase: boolean;
  accessibility?: ApiAccessMode;
  repositoryType?: string;
  oasFilePath?: string;
  isPublicRepo?: boolean;
  context: string;
  endpointUrl: string;
  proxyConfig?: ProxyConfig;
}

export interface ProxyConfig {
  apiDirectory: string;
  sourceRepoUrl: string;
  sourceRepoBranch: string;
}

export enum ProxySource {
  SOURCE_REPO = 'SOURCE_REPO',
  OAS = 'OAS',
  NONE = 'NONE',
  MCP = 'MCP',
}

export interface NewByoiComponent {
  componentName: string;
  displayName: string;
  description: string;
  componentType: string;
  port: number | null;
  imageUrl: string;
  registryId?: string;
  componentSubType?: string;
  labels?: string;
}

export interface NewCloudEditorComponent {
  imageUrl: string;
  registryId?: string;
  sourceCommitHash?: string;
  initialScaffoldPrompt?: string;
  initialScaffoldSteps?: string;
  proxyId?: string;
}

export interface ByocWebAppDockerfileLessComponent {
  displayName: string;
  componentName: string;
  description: string;
  labels: string;
  componentType: string;
  accessibility: string;
  byocWebAppsConfig: {
    dockerContext: string;
    srcGitRepoUrl: string;
    srcGitRepoBranch: string;
    webAppType: string;
    webAppBuildCommand: string;
    webAppPackageManagerVersion: string;
    webAppOutputDirectory: string;
  };
  bitbucketCredentialId?: string;
  buildPreset?: BuildPresetType;
  isPublicRepo?: boolean;
}

export interface BYOCAppType {
  id: ComponentDisplayBYOCTypes;
  name: string;
  info?: string;
  summary: string;
  disabled?: boolean;
}

export interface ComponentTemplate {
  id: ComponentTemplateType;
  title: React.ReactNode;
  description: React.ReactNode;
  isComingSoon?: boolean;
  isPreview?: boolean;
  icon?: string;
  hidden?: boolean;
  deprecated?: boolean;
  displayName?: string;
}

export interface IComponentBase {
  projectId: string;
  id: string;
  name: string;
  handler: string;
  displayName: string;
  displayType: string;
  lastBuildDate?: string;
  version: string;
  createdAt: string;
  orgHandler: string;
  description: string;
  initStatus?: ComponentCreationStatus;
  status: string;
  statusMessage?: string;
  creationProgressPercentage?: number;
  componentSubType?: string;
  isDevantComponent?: boolean;
  isExternalRepoComponent?: boolean;
  /**
   * Not a component at all: a placeholder for work sitting in the cloud editor
   * that has never been deployed, so no component exists for it yet. Rendered as
   * a distinct row that only offers "open the editor". Nothing may call the
   * component APIs with one of these; there is no id on the server to call with.
   */
  isUndeployedDraft?: boolean;
}

export enum ComponentAsyncCreationStatus {
  IN_PROGRESS = 'IN-PROGRESS',
  SUCCESSFUL = 'SUCCESSFUL',
  FAILED = 'FAILED',
}

export interface ComponentSummary {
  id: string;
  name: string;
  status: string;
  displayName: string;
  displayType: string;
  projectId: string;
  handler: string;
  componentSubType?: string;
  isPrebuilt?: boolean;
}

export interface ComponentRepository {
  nameApp: string;
  nameConfig?: string;
  branch: string;
  branchApp: string;
  organizationApp: string;
  organizationConfig?: string;
  isUserManage: boolean;
  appSubPath: string;
  isAuthorizedRepo: boolean;
  byocBuildConfig?: BYOCBuildConfig | null;
  buildpackConfig?: BuildpackBuildConfig[];
  gitProvider: string;
  byocWebAppBuildConfig?: WeAppBuildConfig | null;
  bitbucketServerUrl?: string;
  testRunnerConfig?: TestRunnerConfig | null;
  serverUrl?: string;
  projectApp?: string; // Azure DevOps project name
  isBuildConfigurationMigrated: boolean;
}

export interface ContainerImage {
  imageId: string;
  containerId: string;
  image: {
    imageVersion: string;
    imageName: string;
    imageRegistry: string;
    imageRegistryId: string;
    tagName: string;
    tags: string[];
    committer: string;
    commitMsg: string;
    gitHash: string;
  };
}

export interface AppEnvVersion {
  environmentId: string;
  releaseId: string;
  release: {
    id: string;
    metadata: {
      choreoEnv: string;
    };
    environmentId: string;
    environment: string;
    gitHash: string | null;
    gitOpsHash: string | null;
  };
}

export interface CellDiagram {
  data: string;
  errorName: string;
  message: string;
  success: boolean;
}

export interface LinePosition {
  line: number;
  offset: number;
}

export interface CMLocation {
  filePath: string;
  startPosition: LinePosition;
  endPosition: LinePosition;
}

export interface Service {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  annotation: any;
  path: string;
  serviceId: string;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  resources: any[];
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  remoteFunctions: any[];
  serviceType: string;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  dependencies: any[];
  deploymentMetadata: DeploymentMetadata;
  elementLocation: CMLocation;
  isNoData?: boolean;
  dataInProgress?: boolean;
}

export interface DeploymentMetadata {
  gateways: {
    internet: {
      isExposed: boolean;
      tooltip?: string;
    };
    intranet: {
      isExposed: boolean;
      tooltip?: string;
    };
  };
}

export interface ComponentModel {
  packageId: {
    name: string;
    org: string;
    version: string;
  };
  services: Map<string, Service>;
  entities: Map<string, Entity>;
  hasCompilationErrors: boolean;
}

export interface GetComponentModelResponse {
  componentModels: {
    [key: string]: ComponentModel;
  };
  diagnostics?: ComponentModelDiagnostics[];
}

export interface ComponentModelDiagnostics {
  name: string;
  message?: string;
  severity?: string;
}

export interface ComponentModelResponse {
  componentModels: { [key: string]: ComponentModel };
  diagnostics: ComponentModelDiagnostics[];
  isCellDiagramInProgress: boolean;
}

export interface Entity {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  attributes: any[];
  inclusions: string[];
  elementLocation: Location;
  isAnonymous: boolean;
}

export interface ComponentApiVersion {
  apiVersion: string;
  proxyName: string;
  proxyUrl: string;
  proxyId: string;
  id: string;
  state: string;
  latest: boolean;
  branch: string;
  appEnvVersions: AppEnvVersion[];
  accessibility: ApiAccessMode;
  cellDiagram?: CellDiagram;
  autoDeployEnabled: boolean;
  versionId?: string;
}

export interface DeploymentTrack {
  id: string;
  createdAt: string;
  updatedAt: string;
  apiVersion: string;
  branch: string;
  description: string;
  componentId: string;
  latest: boolean;
  versionStrategy: string;
  autoDeployEnabled: boolean;
}

export interface IComponent extends IComponentBase {
  description: string;
  ownerName: string;
  labels: string[];
  type: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  developmentGroup: string;
  gitRepoUrl: string;
  branch: string;
  status: string;
  endPoint: string;
  orgUuid: string;
  orgHandler: string;
  apiId: string;
  bitbucketServerUrl?: string;
  repository: ComponentRepository | null;
  apiVersions: ComponentApiVersion[] | null;
  deploymentTracks: DeploymentTrack[] | null;
  httpBased: boolean;
  isMigrationCompleted: boolean;
  skipDeploy: boolean;
  isUnifiedConfigMapping?: boolean;
  endpointShortUrlEnabled: boolean;
  serviceAccessMode?: NetworkVisibilityType;
  componentSubType?: string;
  isPrebuilt?: boolean;
  originCloud?: string;
}

export interface IDevantComponent extends IComponent {
  isDevantComponent?: boolean;
}

export interface ExecutionArgument {
  argumentName: string;
  argumentValue: string;
}

export interface ExecutionArgumentPayload {
  argument_name: string;
  argument_value: string;
}

export interface RuntimeArgument {
  name: string;
  type: string;
  prefix?: string;
  displayName?: string;
  description?: string;
  delimiter?: string;
  values?: string[];
  repeat?: boolean;
  required?: boolean;
}

export enum RuntimeArgumentType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  ENUM = 'enum',
}

export interface ReleaseDetails {
  orgId: string;
  projectId: string;
  componentId: string;
  releaseId: string;
  arguments?: ExecutionArgumentPayload[];
}

export interface UpdateComponentRequest {
  id: string;
  displayName?: string;
  description?: string;
  version?: string;
  labels?: string;
  serviceAccessMode?: NetworkVisibilityType;
}

export interface UpdateComponentResponse {
  id: string;
  name: string;
  handler: string;
  description: string;
  displayType: string;
  displayName: string;
  orgId: number;
  orgHandler: string;
  version: string;
  labels: string[];
  createdAt: string;
  updatedAt: string;
  projectId: string;
  apiId: string;
}

export interface ReleaseMgtDeployment {
  releaseMgtDeploymentName: string;
  releaseMgtReleaseId: string;
  releaseMgtDeploymentId: string;
  releaseMgtReleaseName: string;
  configMappingRevision: number;
  schemaBasedConfigRevision: number;
  apiSettings: string | null;
}

export interface IDeployment {
  environmentId: string;
  configCount?: number;
  apiId: string;
  releaseId: string;
  build: {
    buildId: string;
    deployedAt: string;
    commit: ICommit;
    runId?: string;
    sourceConfigMigrationStatus?: SourceConfigMigrationStatus | null;
  };
  invokeUrl: string;
  versionId: string;
  deploymentStatus: DeploymentStatus;
  deploymentStatusV2: DeploymentStatus | null;
  version: string;
  cron: string;
  cronTimezone: string;
  apiRevision?: {
    id: string;
    displayName: string;
  };
  imageUrl: string | null;
  webAppLocalDevEnabled?: boolean;
  releaseMgtDeployment?: ReleaseMgtDeployment;
}

export interface SourceConfigMigrationStatus {
  canMigrate: boolean;
  existingFileName: string | null;
  existingFileSchemaVersion: string | null;
}

export interface IExecutionConfigs {
  cronjobFrequency: string;
  cronjobTimezone: string;
  cronjobAllowConcurrency: boolean;
  timeoutSeconds: number;
  retryCount: number;
}

export interface ICommit {
  author: {
    name: string;
    date: string;
    email: string;
    avatarUrl: string;
  };
  sha: string;
  message: string;
  isLatest: boolean;
}

export interface ITag {
  name: string;
  commitSha: string;
  url: string;
}

export interface IDeploymentTrackImage {
  imageId: string;
  createdAt: string;
  updatedAt: string;
  commitHash: string;
  commitMessage: string;
  author: {
    name: string;
    date: string;
    email: string;
    avatarUrl: string;
  };
  runId: string;
  builtAt: string;
}

export enum BuildSteps {
  FirstStep = 1,
  SecondStep = 2,
}
export interface IBuildByVersion {
  id: string;
  createdDate: string;
  versionId: string;
  buildId: string;
  commitHash: string;
  commitMessage: string;
  revisions: RevisionInfo[];
}

export interface BuildInfo {
  id: string;
  versionId: string;
  buildId: string;
}

export interface RevisionInfo {
  revisionId: string;
  createdDate: string;
  description: string | null;
  environments: string[];
}

export interface CreateBuild {
  id: string;
  componentId: string;
  versionId: string;
  buildId: string;
}

export interface CreateBuildRevision {
  id: string;
  componentId: string;
  versionId: string;
  buildId: string;
  commitHash: string;
  revisions: {
    revisionId: string;
    environments: string;
  };
  environments: {
    id: string;
    revisions: {
      revisionId: string;
    };
  };
}

export interface RevisionDeploymentInfo extends RevisionInfo {
  name?: string;
  vhost?: string;
  displayOnDevportal?: boolean;
  deployedTime?: string;
  successDeployedTime?: string;
}
export interface APIProxyBuild {
  id: string;
  versionId: string;
  buildId: string;
  baseRevisionId: string;
  deployedRevisionId: string;
  revisions: RevisionDeploymentInfo[];
}

export interface BuildAndEnvironment {
  buildId: string;
  environment: Environment;
  revisionId: string;
}

export interface Branch {
  name: string;
  apiVersion: string[];
}

export interface RepoBranch {
  name: string;
  isDefault: boolean;
}
export interface RepoDirectory {
  name: string;
}

export interface BasicInvokeData {
  environmentId: string;
  environmentName: string;
  invokeUrl: string;
}

export interface ProxyDeployment {
  apiId: string;
  environment: {
    choreoEnv: string;
    name: string;
    id: string;
  };
  lifecycleStatus: string | null;
  version: string | null;
  invokeUrl: string | null;
  endpoint: string | null;
  sandboxEndpoint: string;
  apiRevision: {
    id: string;
    displayName: string;
    createdTime: string;
  } | null;
  build: {
    id: string;
    baseRevisionId: string;
    deployedRevisionId: string;
  };
  deployedTime: string;
  successDeployedTime: string;
}

export interface ChangeAccessibilityResponse {
  updateApiAccessibility: string;
}

export interface ComponentLabels {
  componentsLabels: string[];
}

export interface BuildConfig {
  dockerContext: string;
  dockerfilePath: string;
  oasFilePath: string;
}

export interface WeAppBuildConfig {
  id: string;
  containerId: string;
  componentId: string;
  repositoryId: string;
  dockerContext: string;
  webAppType: string;
  buildCommand: string;
  packageManagerVersion: string;
  outputDirectory: string;
  enableTrivyScan: boolean;
}

export interface BYOCBuildConfig extends BuildConfig {
  id: string;
  isMainContainer: boolean;
  containerId: string;
  componentId: string;
  repositoryId: string;
}

export interface BuildpackBuildConfig {
  versionId: string;
  buildContext: string;
  languageVersion: string;
  isUnitTestEnabled: boolean;
  pullLatestSubmodules: boolean;
  enableTrivyScan: boolean;
  buildpack: {
    id: string;
    language: string;
  };
  keyValues?: BuildEnvironmentVariable[];
}

export interface AutoDeploymentStatus {
  success: boolean;
  message: string;
}

export interface GetAutoBuildResponse {
  success: boolean;
  message: string;
  data: {
    autoBuildId: string;
    autoBuildEnabled: boolean;
    componentId: string;
    versionId: string;
    envId: string;
  };
}

export interface FailedChecks {
  bc_category: string | null;
  bc_check_id: string | null;
  benchmarks: string | null;
  caller_file_line_range: string | null;
  caller_file_path: string | null;
  check_class: string | null;
  check_id: string | null;
  check_name: string | null;
  check_result: {
    result: string | null;
    results_configuration: string | null;
  };
  code_block: any[];
  connected_node: string | null;
  description: string | null;
  details: any[];
  entity_tags: string | null;
  evaluations: string | null;
  file_abs_path: string | null;
  file_line_range: any[];
  file_path: string | null;
  fixed_definition: string | null;
  guideline: string | null;
  repo_file_path: string | null;
  resource: string | null;
  resource_address: string | null;
  severity: string | null;
  short_description: string | null;
  vulnerability_details: string | null;
}
export interface CheckovScan {
  check_type: string;
  results: {
    failed_checks: FailedChecks[];
  };
  summary: {
    passed: number;
    failed: number;
    skipped: number;
    parsing_errors: number;
    resource_count: number;
    checkov_version: string;
  };
}
export enum CheckovCode {
  CKV_DOCKER_3 = 'CKV_DOCKER_3',
  CKV_DOCKER_8 = 'CKV_DOCKER_8',
}

export interface AsgardeoWebhookData {
  organization: string;
  clientId: string;
  clientSecret: string;
  tokenEndpointHost: string;
  hubURL: string;
  keyServiceURL: string;
}

export interface IMediationServiceData {
  commitHash: string;
  mediationComponentId: string;
  mediationComponentName: string;
  mediationComponentVersion: string;
  revisionId?: string;
}

export interface EnvironmentBuildMapItem {
  envId: string;
  buildId: string;
}

export interface EnvironmentBuildMap {
  [key: string]: EnvironmentBuildMapItem;
}

export interface EndpointStateReason {
  code: string;
  message: string;
  details: string;
  workerId: string;
}

export interface ComponentEndpoint {
  id: string;
  name?: string;
  createdAt: string;
  updatedAt: string;
  releaseId: string;
  environmentId: string;
  displayName: string;
  port: number;
  type: string;
  apiContext: string;
  apiDefinitionPath: string;
  invokeUrl: string;
  visibility: string;
  networkVisibilities: string[];
  hostName: string;
  isAutoGenerated: boolean;
  apimId: string;
  apimRevisionId: string;
  internalGwRevisionId?: string;
  externalGwRevisionId?: string;
  apimName: string;
  projectUrl: string;
  organizationUrl: string;
  publicUrl: string;
  state: string;
  stateReason: EndpointStateReason;
  deletedAt: string;
  isTemporaryExposed?: boolean;
  temporaryExposedUrl?: string; // This is used to store the temporary exposed URL for organization level visibility
  configurationGroupName?: string;
  signature?: string;
  hasShortUrl: boolean;
  shortUrlReassignState: string;
  defaultPublicUrl: string;
  defaultOrganizationUrl: string;
  isScopeAdded?: boolean; // This is used to check whether the scope is added via the definition
  generationSource?: string;
}

export interface ErroredEndpointInfo {
  endpointId: string;
  endpointDisplayName: string;
  apiLifecycleState: string;
  subscriptionCount: number;
}

export enum EndpointType {
  GraphQL = 'GraphQL',
  GRPC = 'GRPC',
  REST = 'REST',
  WebSocket = 'WS',
}

export const userProvideEndpointTypes = [
  EndpointType.REST,
  EndpointType.GraphQL,
  EndpointType.WebSocket,
];

export enum ProjectType {
  MultiRepository = 'Multi-Repo',
  MonoRepository = 'Monorepo',
}

export enum NetworkVisibilityType {
  Project = 'Project',
  Organization = 'Organization',
  Public = 'Public',
}

export interface EndpointFileContents {
  endpointYaml: {
    path: string;
    data: string;
  };
  apiSchemas: {
    path: string;
    data?: string;
  }[];
}

export interface DeploymentContainerImage {
  image_id: string;
  image: {
    git_hash_commit_timestamp: string;
    git_hash: string;
  };
}

export interface DeploymentError {
  outcome?: string;
  reason?: string;
  scale_to_zero_incompatible_endpoint?: {
    display_name: string;
    port: number;
    api_type: string;
    visibility: string;
    errors: string[];
  }[];
}
export interface DeploymentHistory {
  ID: string;
  CreatedAt: string;
  app_environment_id: string;
  container_images?: DeploymentContainerImage[];
  deployment_status?: null | DeploymentError;
  change_message?: string;
}

export interface ReleaseDeployment {
  id: string;
  release_mgt_id: string;
  environment_id: string;
  deployment_name: string;
  attempt: number;
  config_revision: number;
  status: string;
  comment: string;
  deployed_at: string;
  deployed_by: string;
  release_name: string;
  commit_hash: string;
  component_configs: {
    config_mapping_revision: number;
    schema_based_config_revision: number;
    api_settings: string | null;
  };
  created_at: string;
}

export interface ComponentEndpointApiDefinition {
  content: string;
}

export interface ComponentEndpointApiDefinitionByEndpointNameAndCommitHash {
  content: string;
}

export interface EndpointInvokeUrlMap {
  invokeUrl: string;
  type: NetworkVisibilityType;
}

export enum EndpointDeploymentStatus {
  Pending = 'Pending',
  InProgress = 'Progressing',
  Active = 'Active',
  Error = 'Error',
}

export interface EndpointState {
  countString: string;
  status: EndpointDeploymentStatus | null;
  statusVariant: ChipProps['color'];
  activeCount: number;
  inProgressCount: number;
  pendingCount: number;
  errorCount: number;
}

export interface GenerateEndpointRequest {
  releaseId: string | null;
  commitHash: string | null;
  dryRun?: boolean;
}

export interface ReassignDefaultUrlRequest {
  componentId: string | undefined;
  versionId: string | null;
  releaseId: string;
  targetEndpointId: string;
}

export interface ComponentTypeCount {
  componentType: ComponentDisplayType;
  count: number;
}

// eslint-disable-next-line prettier/prettier
export interface RepositoryContents extends RenderTree {}

export interface VisibilityLevel {
  isPublicRepo: boolean;
}
export interface RepositoryVisibilityLevel {
  success: boolean;
  data: VisibilityLevel;
  message: string;
}

export interface TestRunnerComponent {
  name: string;
  description: string;
  labels: string;
  componentType: string;
  testRunnerConfig: {
    postmanDirectory?: string;
    srcGitRepoUrl?: string;
    srcGitRepoBranch?: string;
    testRunnerType?: string;
    dockerContext?: string;
  };
  bitbucketCredentialId?: string;
  isPublicRepo?: boolean;
}

export interface TestRunnerConfig {
  dockerContext?: string;
  postmanDirectory?: string;
  testRunnerType: string;
}

export enum TreeNodeType {
  FILE = 'blob',
  TREE = 'tree',
}

export interface APIVersionEndpoints {
  apiVersion: string;
  endpoints: ComponentEndpoint[];
}

export enum ShortUrlReassignStates {
  REASSIGNABLE = 'Reassignable',
  LOCKED = 'Locked',
  NOT_APPLICABLE = 'NotApplicable',
}

export interface UpdateDeploymentTrackRequest {
  componentId: string;
  deploymentTrackId: string;
  branch?: string | null;
  description?: string | null;
  enableAutoDeploy?: boolean | null;
}

export interface ComponentCreationLoadingBoxData {
  text: string;
  progress: number;
}

export enum ComponentListingTabs {
  COMPONENTS = 'Components',
  ARCHITECTURE_DIAGRAM = 'Architecture Diagram',
}

export enum ThemeSource {
  HTML = 'HTML',
  CSS = 'CSS',
  MAIN_CSS = 'MAIN_CSS',
  ASSESTS = 'ASSESTS',
  THEME_CONFIG = 'THEME_CONFIG',
}

export interface BuildPipelineMetadata {
  clusterId: string;
  hasWorkflowEnabled: boolean; // This is true if argo workflow is enabled for the component
  orgType: string;
  workflowType: string;
}

export type OverviewStep =
  | 'gotoVSCode'
  | 'initialBuild'
  | 'afterBuild'
  | 'initialBuildWaiting'
  | 'initialBuildFailed';
